import Vue from 'vue';
import api from '../bu/api';
import moment from 'moment';

export default {
	namespaced: true,

	state: () => ({
		items: {},
		itemsArray: [],
		specs: {
			// item-id to array
		},
		paging: null,
	}),

	getters: {
		items(state) {
			return state.itemsArray;
		},
		itemMap(state) {
			return state.items;
		},
		specMap(state) {
			return state.specs;
		},
		paging(state) {
			return state.paging;
		},
	},

	actions: {
		reset({commit}) {
			commit('reset');
		},
		async duplicateItem({commit}, itemId) {
			const response = await api.duplicateItem(itemId);
			const item = response.data;
			commit('setItem', item);
			item.specs.forEach((spec) => {
				commit('setSpec', spec);
			});
		},
		async getItems({commit}, { page, keyword, category_ids, status }) {
			const response = await api.getItems(page, keyword, category_ids, status);
			const result = response.data.data;
			commit('reset');
			commit('setItems', result);
			commit('setPaging', response.data);
			result.forEach((item) => {
				item.specs.forEach((spec) => {
					commit('setSpec', spec);
				});
			});
		},
		async getAllItems({commit}) {
			const response = await api.getAllItems();
			commit('setItems', response.data);
			response.data.forEach((item) => {
				item.specs.forEach((spec) => {
					commit('setSpec', spec);
				});
			})
			return response.data.map((item) => item.id);
		},
		async deleteItem({commit}, itemId) {
			const response = await api.deleteItem(itemId);
			commit('deleteItem', itemId);
		},
		async deleteImage({commit}, {itemId, imageId}) {
			const response = await api.deleteImage(imageId);
			commit('deleteItemImage', {itemId, imageId});
		},
		async saveItem({commit}, {item, photoFiles}) {
			const response = await api.saveItem(item, photoFiles);
			commit('setItem', response.data);
			return response.data;
		},
		async deleteSpecImage({commit}, {itemId, specId}) {
			const response = await api.deleteSpecImage(itemId, specId);
			commit('deleteSpecImage', {itemId, specId});
		},
		// type: visible, hidden
		async setAutoVisibility({commit}, {item, type, datetime}) {
			const response = await api.setAutoVisibility({type, datetime, itemId: item.id});
			const gmtDatetime = moment(datetime).tz('GMT').format('YYYY-MM-DD HH:mm:ss');
			if (type === 'visible') {
				commit('setItemAutoVisibleAt', {item, datetime: gmtDatetime});
			} else if (type === 'hidden') {
				commit('setItemAutoHiddenAt', { item, datetime: gmtDatetime });
			}
		},
		async clearAutoVisibility({commit}, {item}) {
			await api.clearAutoVisibility({itemId: item.id});
			commit('setItemAutoVisibleAt', {item, datetime: null});
			commit('setItemAutoHiddenAt', {item, datetime: null});
		},
		async uploadItemImages({commit}, {item, photoFiles}) {
			const response = await api.uploadItemImages(item.id, photoFiles);
			commit('setItem', response.data);
			return response.data;
		},
		async saveSpec({commit}, {spec, photoFile}) {
			var specToSave = Object.assign({}, spec);
			if (spec.id && spec.id.match && spec.id.match("local")) {
				specToSave.id = null;
			}
			specToSave.provider_logistic_ids = spec.logisticIds.join(',');
			const response = await api.saveItemSpec(specToSave, photoFile);
			commit('setSpec', response.data);
		},
		async showItem({commit}, itemId) {
			const response = await api.showItem(itemId);
			commit('setItemVisibility', {itemId, visible: true});
		},
		async hideItem({commit}, itemId) {
			const response = await api.hideItem(itemId);
			commit('setItemVisibility', {itemId, visible: false});
		},
		initSpec({commit}, itemId) {
			commit('setSpec', {
				id: 'local' + (new Date()).getTime(),
				provider_item_id: itemId,
			});
		},
		updateSpec({commit}, spec) {
			commit('setSpec', spec);
		},
		async removeSpec({commit}, {provider_item_id, id}) {
			if (id.match && id.match('local')) {
				// no op when it's local spec
			} else {
				await api.deleteItemSpec(provider_item_id, id);
			}
			commit('removeSpec', { itemId: provider_item_id, specId: id});
		},
		removeAllSpecs({commit}, itemId) {
			commit('removeAllSpec', itemId);
		},
		updateItemHasPricingRules({commit}, {itemId, hasPricingRules}) {
			commit('updateItemHasPricingRules', {itemId, hasPricingRules});
		},
		updateSpecHasPricingRules({commit}, {itemId, specId, hasPricingRules}) {
			commit('updateSpecHasPricingRules', {itemId, specId, hasPricingRules});
		}
	},

	mutations: {
		updateItemHasPricingRules(state, {itemId, hasPricingRules}) {
			let item = state.items[itemId];
			if (item) {
				item.has_pricing_rules = hasPricingRules;
			}
		},
		updateSpecHasPricingRules(state, {itemId, specId, hasPricingRules}) {
			let spec = state.specs[itemId]?.find((s) => s.id == specId);
			if (spec) {
				spec.has_pricing_rules = hasPricingRules;
			}
		},
		reset(state) {
			state.items = {};
			state.itemsArray = [];
			state.specs = {};
		},
		setSpec(state, spec) {
			if (!state.specs[spec.provider_item_id]) {
				Vue.set(state.specs, spec.provider_item_id, []);
			}
			const index = state.specs[spec.provider_item_id].findIndex((s) => s.id == spec.id)
			if (index > -1) {
				Vue.set(state.specs[spec.provider_item_id], index, spec);
			} else {
				state.specs[spec.provider_item_id].push(spec);
			}
		},
		removeAllSpec(state, itemId) {
			state.specs[itemId] = [];
		},
		removeSpec(state, {itemId, specId}) {
			if (state.specs[itemId]) {
				state.specs[itemId] = state.specs[itemId].filter((spec) => spec.id !== specId);
			}
		},
		setItemVisibility(state, {itemId, visible}) {
			if (state.items[itemId]) {
				state.items[itemId].is_visible = visible;	
			}
		},
		setItem(state, item) {
			item.availableWeekdays?.sort((a, b) => a - b);
			Vue.set(state.items, item.id, item);
			Vue.set(state, 'itemsArray', Object.values(state.items));
		},
		setItems(state, items) {
			items.forEach((item) => {
				item.availableWeekdays?.sort((a, b) => a - b);
				Vue.set(state.items, item.id, item);
			});
			Vue.set(state, 'itemsArray', Object.values(state.items));
		},
		deleteItem(state, itemId) {
			Vue.delete(state.items, itemId);
			Vue.set(state, 'itemsArray', Object.values(state.items));
		},
		deleteItemImage(state, {itemId, imageId}) {
			const item = state.items[itemId];
			const images = item.images.filter((image) => image.id != imageId);
			Vue.set(item, 'images', images);
		},
		deleteSpecImage(state, {itemId, specId}) {
			if (state.specs[itemId]) {
				const spec = state.specs[itemId].find((spec) => spec.id == specId);
				Vue.set(spec, 'image_path', null);
				Vue.set(spec, 'thumbnail_path', null);
			}
		},
		setItemAutoVisibleAt(state, {item, datetime}) {
			Vue.set(item, 'auto_visible_at', datetime);
		},
		setItemAutoHiddenAt(state, {item, datetime}) {
			Vue.set(item, 'auto_hidden_at', datetime);
		},
		setPaging(state, paging) {
			state.paging = paging;
		},
	},
}